import React from 'react';
import { Link } from 'react-router-dom';
import { cleanTournamentName } from '../utils/cleanTournamentName';
import { ROUND_STATUS_LABEL, formatRoundDate } from './round-date';

export interface NextRound {
  id: number;
  number: number;
  name: string;
  startDate: string | null;
  status: string;
}

export interface LeagueCardData {
  id: number;
  name: string;
  discipline: string;
  status: string;
  signupMode?: 'PRIVATE' | 'OPEN' | 'APPROVAL' | 'APPROVAL_EXCEPT_MEMBERS' | null;
  venue?: string | null;
  venueCity?: string | null;
  club?: { slug: string; name: string } | null;
  nextRound?: NextRound | null;
  lastRound?: { id: number; number: number; name: string; startDate: string } | null;
}

const DISCIPLINE_LABELS: Record<string, string> = {
  SINGLES: 'Singiel',
  SINGLES_M: 'Singiel M',
  SINGLES_F: 'Singiel K',
  SINGLES_MIX: 'Singiel Mix',
  DOUBLES: 'Debel',
  DOUBLES_M: 'Debel M',
  DOUBLES_F: 'Debel K',
  DOUBLES_MIX: 'Debel Mix',
  MIXED: 'Mikst',
  CUSTOM: 'Inna',
};

export function disciplineLabel(discipline: string): string {
  return DISCIPLINE_LABELS[discipline] ?? discipline;
}

export function disciplineVariant(discipline: string): 'singles' | 'doubles' | 'mixed' {
  if (['DOUBLES', 'DOUBLES_M', 'DOUBLES_F'].includes(discipline)) {
    return 'doubles';
  }
  if (['MIXED', 'SINGLES_MIX', 'DOUBLES_MIX'].includes(discipline)) {
    return 'mixed';
  }

  return 'singles';
}

export function venueLine(league: LeagueCardData): string | null {
  const parts = [league.venue?.trim(), league.venueCity?.trim()].filter(
    (s): s is string => Boolean(s && s.length > 0)
  );
  if (parts.length === 0) {
    return null;
  }

  return parts.join(' · ');
}

interface Props {
  league: LeagueCardData;
  ctaLabel?: string;
  ctaHash?: string;
  signupOpen?: boolean;
}

export function LeagueCard({ league, ctaLabel, ctaHash, signupOpen = false }: Props) {
  const venue = venueLine(league);
  const club = league.club && league.club.slug !== 'global' ? league.club : null;
  const next = league.nextRound ?? null;
  const variant = disciplineVariant(league.discipline);

  const resolvedCtaLabel = ctaLabel ?? (signupOpen ? 'Zapisz się' : 'Szczegóły');
  const ctaTo = ctaHash
    ? `/leagues/${league.id}${ctaHash}`
    : signupOpen
    ? `/leagues/${league.id}#zapisy`
    : `/leagues/${league.id}`;

  return (
    <li
      className={`event-card league-card league-card--${variant}${
        next ? ' league-card--has-next' : ''
      }${signupOpen ? ' league-card--signup-open' : ''}`}
    >
      <div className="league-card__eyebrow">
        <span className={`league-card__discipline-tag league-card__discipline-tag--${variant}`}>
          {disciplineLabel(league.discipline)}
        </span>
        {signupOpen && (
          <span className="league-card__signup-tag">
            <span className="signup-pulse-dot" aria-hidden="true">●</span>
            Zapisy otwarte
          </span>
        )}
      </div>

      <h2 className="event-card-name league-card__name">
        <Link to={`/leagues/${league.id}`} className="league-card__name-link">
          {cleanTournamentName(league.name)}
        </Link>
      </h2>

      {next && (
        <Link
          to={`/leagues/${league.id}/rounds/${next.id}`}
          className="league-card__next"
          aria-label={`Najbliższa kolejka: ${next.name}${
            next.startDate ? `, ${formatRoundDate(next.startDate)}` : ''
          }`}
        >
          <span className="league-card__next-eyebrow">Najbliższa kolejka</span>
          <span className="league-card__next-name">{next.name}</span>
          <span className="league-card__next-date">
            {next.startDate
              ? formatRoundDate(next.startDate)
              : ROUND_STATUS_LABEL[next.status] ?? 'Wkrótce'}
          </span>
        </Link>
      )}

      <dl className="league-card__meta">
        {club && (
          <div className="league-card__row league-card__row--org">
            <dt className="league-card__label">Organizator</dt>
            <dd className="league-card__value">
              <Link to={`/clubs/${club.slug}`} className="league-card__org-link">
                {club.name}
              </Link>
            </dd>
          </div>
        )}
        {venue && (
          <div className="league-card__row league-card__row--venue">
            <dt className="league-card__label">Miejsce</dt>
            <dd className="league-card__value">
              <span className="league-card__pin" aria-hidden="true" />
              {venue}
            </dd>
          </div>
        )}
      </dl>

      {!club && !venue && !next && (
        <p className="league-card__value league-card__value--muted">
          Liga otwarta — szczegóły wkrótce
        </p>
      )}

      <Link to={ctaTo} className="event-card-link league-card__cta">
        {resolvedCtaLabel}
      </Link>
    </li>
  );
}
